import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useState } from "react";
import type { Need } from "@instead/shared";
import { api } from "../lib/api.ts";
import { useT } from "../lib/i18n.ts";
import { goBackTo } from "../lib/nav.ts";
import { Ic } from "../components/Icons.tsx";
import { Dialog, InlineName, Toast, useToast } from "../components/Ui.tsx";

/** Needs (§4.4) edited in one place (round 5): built-ins are fixed, your own
 *  are renamed in place, added by typing into a row, removed. The item and
 *  place forms link here and keep their drafts. */
export function Needs() {
  const t = useT();
  const qc = useQueryClient();
  const nav = useNavigate();
  const search = useSearch({ strict: false }) as { back?: string };
  const { data } = useQuery({ queryKey: ["needs"], queryFn: api.needs.list });
  const needs = data?.needs ?? [];
  const [editing, setEditing] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [confirm, setConfirm] = useState<Need | null>(null);
  const [toast, say] = useToast();
  const refresh = async () => { await qc.invalidateQueries({ queryKey: ["needs"] }); await qc.invalidateQueries({ queryKey: ["items"] }); };

  async function rename(n: Need, name: string) {
    setEditing(null);
    if (!name.trim() || name.trim() === n.name) return;
    await api.needs.update(n.id, name.trim());
    await refresh();
    say(t("saved"));
  }

  async function create(name: string) {
    setAdding(false);
    if (!name.trim()) return;
    await api.needs.create(name.trim());
    await refresh();
    say(t("saved"));
  }

  return (
    <div>
      <div className="screen-head">
        <button className="icon-btn" aria-label={t("back")} onClick={() => void goBackTo(nav, search.back, "/settings/shelf")}>{Ic.back()}</button>
        <h1 className="title">{t("needs.title")}</h1>
        <button className="btn btn-primary" style={{ minHeight: 36 }} onClick={() => setAdding(true)}>{Ic.plus({ size: 14 })} {t("needs.new")}</button>
      </div>
      {needs.map((n) => (
        <div key={n.id} className="nav-row" style={{ cursor: "default" }}>
          {editing === n.id ? (
            <InlineName label={t("cats.name")} value={n.name} onDone={(v) => rename(n, v)} />
          ) : (
            <>
              <span className="chip chip-on" style={{ pointerEvents: "none" }}>{n.name}</span>
              {!n.builtIn && <button className="icon-btn" style={{ minWidth: 32, minHeight: 32, padding: 0 }} aria-label={`${t("edit")} ${n.name}`} onClick={() => setEditing(n.id)}>{Ic.edit({ size: 14 })}</button>}
              <span className="grow" />
              {n.usageCount ? <span className="meta">{t("needs.used", { n: n.usageCount })}</span> : null}
              {!n.builtIn && <button className="icon-btn danger" aria-label={`${t("remove")} ${n.name}`} onClick={() => setConfirm(n)}>{Ic.x({ size: 14 })}</button>}
            </>
          )}
        </div>
      ))}
      {adding && (
        <div className="nav-row" style={{ cursor: "default" }}>
          <InlineName label={t("cats.name")} value="" placeholder={t("needs.new")} onDone={create} />
        </div>
      )}

      {confirm && (
        <Dialog title={t("needs.deleteQ", { name: confirm.name })} onClose={() => setConfirm(null)}>
          <div className="dialog-actions">
            <button className="btn" onClick={() => setConfirm(null)}>{t("keep")}</button>
            <button className="btn btn-danger" onClick={async () => { await api.needs.remove(confirm.id); setConfirm(null); await refresh(); }}>{t("remove")}</button>
          </div>
        </Dialog>
      )}
      <Toast msg={toast} />
    </div>
  );
}
